import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Info } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import {
  PmcExplainerModal,
  type PmcMetricKey,
} from "@/components/dashboard/PmcExplainerModal";
import type { Dashboard } from "@/types";

interface Props {
  pmc: Dashboard["pmc"];
  currentWeekNumber: number | null;
  currentPhase: string | null;
}

export function PmcCard({ pmc, currentWeekNumber, currentPhase }: Props) {
  const { t } = useTranslation();
  const [openMetric, setOpenMetric] = useState<PmcMetricKey | null>(null);

  const tsbStatus = pmc.interpretation?.tsb.status ?? "good";
  const tsbLabel = pmc.interpretation?.tsb.status_label ?? "";
  const rampLabel = pmc.interpretation?.ramp_rate?.status_label ?? null;

  return (
    <>
      <Card>
        <div className="flex items-center justify-between">
          <SectionLabel>{t("dashboard.state_pmc")}</SectionLabel>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpenMetric("all")}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-taupe hover:text-amber-dark"
              aria-label={t("pmc.info_short")}
            >
              <Info className="h-3.5 w-3.5" strokeWidth={1.8} />
              {t("pmc.info_short")}
            </button>
            <Link
              to="/stats"
              className="text-[11px] font-medium text-amber-dark hover:underline"
            >
              {t("pmc.chart_link")}
            </Link>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <MetricTile
            label="CTL"
            value={Math.round(pmc.current_ctl)}
            sub={t("dashboard.fitness")}
            onClick={() => setOpenMetric("ctl")}
          />
          <MetricTile
            label="ATL"
            value={Math.round(pmc.current_atl)}
            sub={t("dashboard.fatigue")}
            onClick={() => setOpenMetric("atl")}
          />
          <MetricTile
            label="TSB"
            value={`${pmc.current_tsb > 0 ? "+" : ""}${Math.round(pmc.current_tsb)}`}
            sub={t("dashboard.form")}
            tone={
              tsbStatus === "danger"
                ? "danger"
                : tsbStatus === "caution"
                  ? "accent"
                  : "default"
            }
            onClick={() => setOpenMetric("tsb")}
          />
        </div>

        {/* Quick status line */}
        {tsbLabel ? (
          <button
            type="button"
            onClick={() => setOpenMetric("tsb")}
            className="mt-3 flex w-full items-center justify-between gap-2 rounded-md bg-cream/70 px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-cream"
          >
            <span className="text-taupe">{t("pmc.form_label")}</span>
            <span
              className={
                tsbStatus === "danger"
                  ? "font-medium text-coral"
                  : tsbStatus === "caution"
                    ? "font-medium text-amber-dark"
                    : "font-medium text-navy-deep"
              }
            >
              {tsbLabel}
              {rampLabel ? (
                <span className="ml-1 font-normal text-taupe">· {rampLabel.toLowerCase()}</span>
              ) : null}
            </span>
          </button>
        ) : null}

        {currentWeekNumber ? (
          <div className="mt-3 flex items-center justify-between border-t border-cream-border pt-2 text-[12px] text-taupe">
            <span>{t("pmc.week_label")}</span>
            <Link
              to={`/plan/week/${currentWeekNumber}`}
              className="inline-flex items-center gap-1 font-medium text-navy-deep hover:text-amber-dark"
            >
              {currentWeekNumber}
              {currentPhase ? <> · {currentPhase}</> : null}
              <ChevronRight className="h-3 w-3" strokeWidth={2} />
            </Link>
          </div>
        ) : null}
      </Card>

      {openMetric ? (
        <PmcExplainerModal
          onClose={() => setOpenMetric(null)}
          initialMetric={openMetric}
          ctl={pmc.current_ctl}
          atl={pmc.current_atl}
          tsb={pmc.current_tsb}
          phase={pmc.phase ?? currentPhase}
          weeksToRace={pmc.weeks_to_race}
          interpretation={pmc.interpretation}
        />
      ) : null}
    </>
  );
}

function MetricTile({
  label,
  value,
  sub,
  tone = "default",
  onClick,
}: {
  label: string;
  value: number | string;
  sub: string;
  tone?: "default" | "accent" | "danger";
  onClick: () => void;
}) {
  const toneCls =
    tone === "danger"
      ? "text-coral"
      : tone === "accent"
        ? "text-amber-dark"
        : "text-navy-deep";
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-1 rounded-2xl border border-cream-border bg-cream-light px-4 py-3 text-left shadow-soft transition-colors hover:border-amber/50"
    >
      <span className="section-label">{label}</span>
      <span className={`metric-number text-[24px] ${toneCls}`}>{value}</span>
      <span className="text-[11px] text-taupe">{sub}</span>
    </button>
  );
}
